'use client';

import Link from 'next/link';
import { signOut } from 'next-auth/react';
import {
  ArrowLeftRight,
  Check,
  ChevronRight,
  HelpCircle,
  LogOut,
  Settings,
} from 'lucide-react';
import {
  startAccountSwitch,
  useAdmin,
} from '@paeffgen/react/crm-documents/feature-auth';
import { BottomSheet, ThemeToggle } from '@paeffgen/react/crm-documents/ui';
import { CustomerContextPanel } from './customer-context-panel';
import { ReportProblemButton } from './report-problem-button';

interface PillSheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Mobile-Kontomenü: rendert den geteilten `CustomerContextPanel` (Konto,
 * Cross-Login, Debitor-Suche, Ansprechpartner) im BottomSheet. Theme-Umschalter
 * und Footer-Aktionen (Übernehmen/Abmelden) sind sheet-spezifisch.
 */
export function PillSheet({ open, onClose }: PillSheetProps) {
  const { isAdmin } = useAdmin();

  const handleLogout = () => signOut({ callbackUrl: '/' });
  const handleSwitchAccount = () => startAccountSwitch();

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel={isAdmin ? 'Cross-Login und Konto' : 'Kontomenü'}
      footer={
        <div className="flex flex-col gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-lg py-3 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              <Check size={16} /> Übernehmen
            </button>
          )}
          <button
            type="button"
            onClick={handleSwitchAccount}
            className="w-full text-secondary hover:bg-zinc-50 rounded-lg py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 border border-zinc-200 transition-colors"
          >
            <ArrowLeftRight size={16} /> Mit anderem Konto anmelden
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full text-red-600 hover:bg-red-50 rounded-lg py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 border border-red-100 transition-colors"
          >
            <LogOut size={16} /> Abmelden
          </button>
        </div>
      }
    >
      <div className="px-4 pt-3 pb-4 space-y-4">
        <CustomerContextPanel variant="sheet" />

        <section className="rounded-xl border border-zinc-200 p-4">
          <p className="mb-2 text-[10px] font-extrabold uppercase tracking-widest text-zinc-500">
            Darstellung
          </p>
          <ThemeToggle className="w-full justify-center" />
        </section>

        <Link
          href="/einstellungen"
          onClick={onClose}
          className="flex items-center gap-3 rounded-xl border border-zinc-200 p-4 text-left transition-colors hover:bg-zinc-50"
        >
          <Settings size={18} className="shrink-0 text-primary" />
          <span className="flex-1 text-sm font-semibold text-secondary">
            Einstellungen
          </span>
          <ChevronRight size={16} className="shrink-0 text-zinc-400" />
        </Link>

        <Link
          href="/informationen"
          onClick={onClose}
          className="flex items-center gap-3 rounded-xl border border-zinc-200 p-4 text-left transition-colors hover:bg-zinc-50"
        >
          <HelpCircle size={18} className="shrink-0 text-primary" />
          <span className="flex-1 text-sm font-semibold text-secondary">
            Hilfe &amp; FAQ
          </span>
          <ChevronRight size={16} className="shrink-0 text-zinc-400" />
        </Link>

        <ReportProblemButton onActivate={onClose} />
      </div>
    </BottomSheet>
  );
}
